import { t } from "i18next";
import { z } from "zod";

import languageCodeList from "../../constants/languages";

import type {
  CreateOfferTranslationResponse,
  OfferTranslationToCreate,
  Translation,
} from "../../api/OfferAPI.types";
import type { LanguageCode } from "../../api/ProjectAPI.types";
import type { Picture, TranslationsIndexes } from "../../types";

export function getOfferTranslationSchema() {
  return z.object({
    language: z.enum(languageCodeList),
    language_uuid: z.string().optional(),
    default: z.boolean(),
    uuid: z.string().optional(),
    name: z
      .string()
      .min(1, t("form:required", { field: t("form:fields.title") })),
    description: z
      .string()
      .min(1, t("form:required", { field: t("form:fields.description") })),
    picture: z.custom<Picture>().optional(),
  });
}

export function getOfferTranslationsFormSchema() {
  return z.object({
    translations: z.array(getOfferTranslationSchema()),
  });
}

export interface OfferTranslationFormField {
  language: LanguageCode;
  language_uuid?: string;
  default: boolean;
  uuid?: string;
  name: string;
  description: string;
  picture?: Picture;
}

export function apiToFormTranslations(
  translations: Translation[],
): OfferTranslationFormField[] {
  return [...translations]
    .sort((a, b) => {
      if (a.default) return -1;
      if (b.default) return 1;
      return a.language.localeCompare(b.language);
    })
    .map((translation) => ({
      language: translation.language as LanguageCode,
      language_uuid: translation.language_uuid,
      default: translation.default,
      uuid: translation.uuid,
      name: translation.name ?? "",
      description: translation.description ?? "",
      picture: translation.picture ?? undefined,
    }));
}

export function createTranslationResponseToFormField(
  response: CreateOfferTranslationResponse,
  language: LanguageCode,
): OfferTranslationFormField {
  return {
    uuid: response.uuid,
    default: response.default,
    language_uuid: response.language_uuid,
    name: response.name ?? "",
    description: response.description ?? "",
    language,
  };
}

export function formToApiTranslations(
  formTranslations: OfferTranslationFormField[],
): {
  translations: OfferTranslationToCreate[];
  translations_indexes: TranslationsIndexes;
} {
  const translations: OfferTranslationToCreate[] = formTranslations.map(
    (field) => ({
      uuid: field.uuid,
      default: field.default,
      language: field.language,
      language_uuid: field.language_uuid ?? "",
      name: field.name,
      description: field.description,
      picture: field.picture,
    }),
  );

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const language_uuid: Record<string, number[]> = {};
  let defaultIndex = 0;

  formTranslations.forEach((field, index) => {
    if (field.language_uuid) {
      language_uuid[field.language_uuid] = [index];
    }
    if (field.default) {
      defaultIndex = index;
    }
  });

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const translations_indexes: TranslationsIndexes = {
    language_uuid,
    default: { true: [defaultIndex] },
  };

  return { translations, translations_indexes };
}
